import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Round price to avoid float noise (e.g. 24.998 when user typed 25). DB uses numeric(10,3). */
export function roundPrice(value: number | string | null | undefined, decimals = 3): number {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(n)) return 0;
  const factor = Math.pow(10, decimals);
  return Math.round(n * factor) / factor;
}

export const isColorDark = (color: string): boolean => {
  if (!color) return false;

  // Handle HSL
  if (color.startsWith('hsl')) {
    const parts = color.match(/\d+(\.\d+)?/g);
    if (parts && parts.length >= 3) {
      // Index 2 is Lightness in HSL
      return parseFloat(parts[2]) < 50;
    }
  }

  // Handle Hex
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  }

  return false;
};

export const toTailwindColor = (color: string): string => {
  if (!color) return "0 0% 100%";

  if (color.startsWith('hsl')) {
    return color.replace(/hsl\(|\)/g, '');
  }

  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  }

  return color;
};
